import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import "./SalesPages.css";

function SalesLeads() {
  const navigate = useNavigate();

  const [salesLeads, setSalesLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSalesLeads();
  }, []);

  const loadSalesLeads = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/sales-leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to load sales leads");

      const data = await res.json();
      setSalesLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load sales leads error:", err.message);
      setSalesLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSalesLeads = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return salesLeads;

    return salesLeads.filter((lead) =>
      [
        lead.status,
        lead.source,
        lead.first_name,
        lead.last_name,
        lead.agency_name,
        lead.phone,
        lead.email,
        lead.website,
        lead.subscription_status,
        lead.subscription_plan,
        lead.billing_status
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [searchTerm, salesLeads]);

  const fullName = (lead) =>
    `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || "No Name";

  const statusClass = (value) =>
    `sales-chip ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Sales Leads</h1>
            <p className="sales-subtitle">
              Track prospective agencies, manage outreach, and monitor subscription interest.
            </p>
          </div>

          <button
            className="sales-primary-btn"
            onClick={() => navigate("/new-sales-lead")}
          >
            + New Sales Lead
          </button>
        </div>

        <div className="sales-toolbar-card">
          <input
            type="text"
            className="sales-search-input"
            placeholder="Search by name, agency, email, phone, source, or plan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>All Sales Leads</h2>
              <p>{filteredSalesLeads.length} lead{filteredSalesLeads.length === 1 ? "" : "s"}</p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Agency</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Subscription</th>
                  <th>Billing</th>
                  <th>Contact</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="sales-empty-cell">
                      Loading sales leads...
                    </td>
                  </tr>
                ) : filteredSalesLeads.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="sales-empty-cell">
                      No sales leads found.
                    </td>
                  </tr>
                ) : (
                  filteredSalesLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="sales-click-row"
                      onClick={() => navigate(`/sales-lead-profile/${lead.id}`)}
                    >
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{fullName(lead)}</div>
                          <div className="sales-secondary">{lead.email || "No email"}</div>
                        </div>
                      </td>

                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{lead.agency_name || "No agency"}</div>
                          <div className="sales-secondary">{lead.website || "No website"}</div>
                        </div>
                      </td>

                      <td>
                        <span className={statusClass(lead.status)}>
                          {lead.status || "Unknown"}
                        </span>
                      </td>

                      <td>{lead.source || "—"}</td>

                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{lead.subscription_status || "—"}</div>
                          <div className="sales-secondary">{lead.subscription_plan || "No plan"}</div>
                        </div>
                      </td>

                      <td>{lead.billing_status || "—"}</td>

                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{lead.phone || "No phone"}</div>
                          <div className="sales-secondary">{lead.city || ""}{lead.city && lead.state ? ", " : ""}{lead.state || ""}</div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default SalesLeads;